import React, { useCallback, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'

import map from 'lodash/map'
import toLower from 'lodash/toLower'

import { useDialog } from '@galo-monitor/dialog'
import { useT } from '@galo-monitor/i18n'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import { useSnackbar } from '@galo-monitor/snackbar'
import { ProjectActions } from '@galo-monitor/stores/project'
import { serializeQueryParams } from '@galo-monitor/utils/request'
import Checkbox from '@galo-monitor/web-components/Checkbox'
import InputSearch from '@galo-monitor/web-components/InputSearch'
import { LinearLoader } from '@galo-monitor/web-components/Loader'
import Screen from '@galo-monitor/web-components/Screen'
import ProjectModal from '@galo-monitor/web-containers/modals/company/ProjectModal'
import ConfirmationModal from '@galo-monitor/web-containers/modals/ConfirmationModal'
import { useRoutes } from '@galo-monitor/web-containers/routes/authenticated'

import ProjectCard from './components/ProjectCard'
import { AddButton, Grid as EvenlySpacedGrid, Header } from './theme'

const ProjectsScreen = () => {
  const t = useT()
  const snackbar = useSnackbar()
  const { createDialog } = useDialog()
  const dispatch = useCallback(useDispatch(), [])
  const { routes, getActionsBySlug } = useRoutes()

  const [queryParams, setQueryParams] = useState({
    page: 1,
    limit: 20,
    softDelete: false
  })

  const { data: projects, isValidating, mutate } = useGaloMonitorApi(`/companies/projects${ serializeQueryParams(queryParams) }`)

  const userActions = useMemo(
    () => getActionsBySlug(routes.projects.slug),
    [getActionsBySlug, routes.projects.slug]
  )

  const onError = useCallback(
    err => snackbar.error(
      t(err?.response?.data?.message || 'unexpected error occurred'), {
        persist: false
      }),
    [snackbar, t]
  )

  const onAddClick = useCallback(
    () => {
      createDialog({
        id: 'create-project',
        Component: ProjectModal,
        props: {
          onSuccess: mutate
        }
      })
    },
    [createDialog, mutate]
  )

  const onEditClick = useCallback(
    project => {
      createDialog({
        id: 'edit-project',
        Component: ProjectModal,
        props: {
          project,
          onSuccess: mutate
        }
      })
    },
    [createDialog, mutate]
  )

  const onSoftDeleteClick = useCallback(
    project => {
      const actionTitle = project.isActive ? {
        present: t('archive'),
        past: t('archived', { gender: 'male' })
      } : {
        present: t('recover'),
        past: t('recovered', { gender: 'male' })
      }

      createDialog({
        id: 'confirmation-project-archivation',
        Component: ConfirmationModal,
        props: {
          title: t('are you sure you want to {action} the "{name}" {this}?', {
            gender: 'male',
            howMany: 1,
            this: t('project', { howMany: 1 }),
            action: actionTitle.present,
            name: project.name
          }),
          buttonTitle: actionTitle.present,
          buttonProps: {
            color: project.isActive ? 'error' : 'primary'
          },
          onConfirm: () => {
            dispatch(ProjectActions.toggleArchiveProject(
              project,
              () => {
                snackbar.success(t('{this} {action} successfully!', {
                  this: t('project', { howMany: 1 }),
                  action: toLower(actionTitle.past)
                }))
                mutate()
              },
              onError
            ))
          }
        }
      })
    },
    [createDialog, dispatch, mutate, onError, snackbar, t]
  )

  const updateQueryParams = useCallback((value, queryName) => {
    setQueryParams(prevState => ({
      ...prevState,
      [queryName]: value
    }))
  }, [])

  const crumbs = useMemo(() => [
    {
      title: routes.projects.title,
      key: 'projects-screen',
      url: '/projects',
      active: true
    }
  ], [routes.projects.title])

  return (
    <Screen title={ routes.projects.title } breadcrumbs={ crumbs }>
      <Header>
        <AddButton
          id="create-project"
          color="primary"
          onClick={ onAddClick }
          // disabled={ loading || disabled }
        >
          {t('register')}
        </AddButton>
        <Checkbox
          detached
          id="soft-delete"
          name="soft-delete"
          label={ t('datagrid show deleted items') }
          checked={ queryParams.softDelete }
          onChange={ () => updateQueryParams(!queryParams.softDelete, 'softDelete') }
          style={ { marginBottom: 0 } }
        />
        <InputSearch
          detached
          value={ queryParams.q }
          onChange={ event => updateQueryParams(event.target.value, 'q') }
          placeholder={ t('datagrid toolbar search tooltip') }
          variant="outlined"
        />
      </Header>
      {isValidating ? <LinearLoader /> : null}
      {projects?.data ? (
        <EvenlySpacedGrid>
          {map(projects?.data, project => (
            <ProjectCard
              key={ project.id }
              project={ project }
              t={ t }
              userActions={ userActions }
              onEditClick={ userActions.delete && onEditClick }
              onDeleteClick={ userActions.delete && onSoftDeleteClick }
              onCustomActionClick={ userActions.delete && onSoftDeleteClick }
            />
          ))}
        </EvenlySpacedGrid>
      ) : null}
    </Screen>
  )
}

export default ProjectsScreen
